'use client';

import type { JSX } from 'react';
import Image from 'next/image';

interface ServiceModel {
  name: string;
  description: string;
}

interface Service {
  name: string;
  description: string;
}

interface ServiceModelsContent {
  subtitle: string;
  title: string;
  models: ServiceModel[];
  servicesTitle: string;
  services: Service[];
  ctaText: string;
}

interface ServiceModelsSectionProps {
  content?: Partial<ServiceModelsContent>;
}

const DEFAULT_CONTENT: ServiceModelsContent = {
  subtitle: 'Nasza oferta',
  title: 'Modele obsługi prawnej dla firm',
  models: [
    {
      name: 'Konsultacje na żądanie',
      description: 'Płacisz za konkretną usługę – sprawdzanie umowy, porada w sprawie, pismo do kontrahenta. Idealne dla firm, które potrzebują prawnika raz na kilka miesięcy.',
    },
    {
      name: 'Pakiet godzin',
      description: 'Wykupujesz określoną liczbę godzin miesięcznie w niższej stawce. Wykorzystujesz na co potrzebujesz – umowy, porady, pisma. Dla firm z regularnymi, ale niezbyt częstymi potrzebami.',
    },
    {
      name: 'Pełny abonament',
      description: 'Stała miesięczna opłata = prawnik na telefon. Sprawdzanie umów, bieżące doradztwo, reprezentacja w prostych sprawach. Dla firm, które potrzebują wsparcia co tydzień.',
    },
  ],
  servicesTitle: 'Co może wchodzić w skład obsługi prawnej?',
  services: [
    { name: 'Umowa', description: 'sprawdzanie, tworzenie, negocjacje' },
    { name: 'Prawo pracy', description: 'Umowy, regulaminy, zwolnienia' },
    { name: 'Spory', description: 'Z kontrahentami, pracownikami, klientami' },
    { name: 'Windykacja', description: 'wezwania, pozwy, egzekucje' },
    { name: 'Sprawy korporacyjne', description: 'Zmiany w spółce, uchwały, KRS' },
    { name: 'Rodo', description: 'dokumentacja, audyty, incydenty' },
  ],
  ctaText: 'Opisz swoją sprawę',
};

export default function ServiceModelsSection({ content = {} }: ServiceModelsSectionProps): JSX.Element {
  const {
    subtitle,
    title,
    models,
    servicesTitle,
    services,
    ctaText,
  } = { ...DEFAULT_CONTENT, ...content };

  const handleScrollToChat = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const chatSection = document.getElementById('chat-hero');
    if (chatSection) {
      chatSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      className="relative py-16 md:py-24 overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #1a1a1a 0%, #242423 50%, #2d2d2b 100%)',
      }}
    >
      {/* Glow effect at bottom center */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center bottom, rgba(245,203,92,0.25) 0%, transparent 70%)',
        }}
      />

      {/* Glow effect at top left - 40% visible */}
      <div
        className="absolute top-[10%] left-0 -translate-x-[60%] w-[600px] h-[900px] pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(245,203,92,0.2) 0%, transparent 60%)',
        }}
      />

      {/* Glow effect at right side ~1/3 from top - 40% visible */}
      <div
        className="absolute top-1/3 right-0 translate-x-[60%] w-[500px] h-[900px] pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(245,203,92,0.18) 0%, transparent 60%)',
        }}
      />

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center">
          {/* Icon - 40px margin bottom */}
          <div className="flex justify-center mb-10">
            <Image
              src="/assets/consultant-logo-2.svg"
              alt="Lawmingo"
              width={40}
              height={40}
              className="h-10 w-auto"
            />
          </div>
          {/* Subtitle - 24px, 20px margin bottom */}
          <p className="text-lawmingo-cream/70 text-base md:text-2xl mb-5">{subtitle}</p>
          {/* Title - font-fraunces text-2xl md:text-5xl, 60px margin bottom */}
          <h2 className="font-fraunces text-2xl md:text-5xl text-lawmingo-cream text-center mb-[60px]">
            {title}
          </h2>
        </div>

        {/* Models grid - 60px from header */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {models.map((model, index) => (
            <div key={index} className="text-center">
              {/* Model name badge - 24px font, all filled */}
              <div className="inline-block rounded-full px-6 py-3 border-2 bg-accent-600 border-accent-600">
                <span className="font-medium text-2xl text-lawmingo-charcoal">
                  {model.name}
                </span>
              </div>
              {/* Model description - 32px margin top, 16px font */}
              <p className="text-lawmingo-cream/70 text-base leading-relaxed mt-8">
                {model.description}
              </p>
            </div>
          ))}
        </div>

        {/* Services section - 80px margin top */}
        <div className="mt-20">
          {/* Services heading - 32px font, 48px margin bottom */}
          <h3 className="text-[32px] font-display text-lawmingo-cream mb-12">
            {servicesTitle}
          </h3>

          {/* Services grid - gap 24px */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-[#FFFDF5] rounded-card p-6 border-2 border-accent-600"
                style={{
                  boxShadow: '0 4px 20px rgba(245,203,92,0.15)',
                }}
              >
                {/* Service name - 18px */}
                <h4 className="font-bold text-lawmingo-charcoal text-lg mb-1">
                  {service.name}
                </h4>
                {/* Service description - 16px */}
                <p className="text-lawmingo-charcoal/70 text-base">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Button - 64px margin top */}
        <div className="text-center mt-16">
          <a
            href="#chat-hero"
            onClick={handleScrollToChat}
            className="inline-block bg-accent-600 text-lawmingo-charcoal font-medium px-8 py-3 rounded-full hover:bg-[#f4af23] transition-colors"
          >
            {ctaText}
          </a>
        </div>
      </div>
    </section>
  );
}
